// The one X-ray catalog: built-in manifests plus whatever runtime lenses this
// tenant has mounted.
//
// Three modules used to each call `createRegistry(MANIFESTS, …)` at module scope.
// That was fine while the catalog was fixed at build time; it is not once a lens
// can be mounted at runtime, for two reasons that pull in opposite directions:
//
//   LAZY, because the tenant is not known at module load. Runtime specs are
//   bucketed per tenant, and `/v1/me` has not resolved when these modules import —
//   a module-scope read lands in the `:default` bucket and silently shows the
//   wrong tenant's lenses, or none.
//
//   CACHED FOR THE SESSION, because a catalog that changes mid-session makes the
//   thing it exists to measure meaningless. The per-trace feed's residual is the
//   honest score for whether a lens claimed anything, and residual computed before
//   a mount is not comparable with residual computed after. One consistent catalog
//   per session is what makes "mount a candidate and watch residual move" a
//   measurement rather than an impression — the same reason `featureFlags` reads
//   its gates once and asks for a reload.
//
// So: resolved on first access, cached, and invalidated explicitly by a mount or
// unmount. `catalogGeneration()` lets a caller notice the invalidation and say a
// reload is needed rather than re-render into a half-swapped catalog.

import { createRegistry } from "../../xray-factory/src/xray/registry.js";
import { MANIFESTS, HOST_WIRED } from "../../xray-factory/xrays/index.js";
import { useSyncExternalStore } from "react";
import { runtimeLensManifests, subscribeRuntimeLenses } from "./xrayRuntimeLensStore.js";

let _registry = null;
let _generation = 0;
let _subscribed = false;
let _unsubscribe = null;

function build() {
  const { manifests, problems } = runtimeLensManifests(null, { reserved: HOST_WIRED });
  if (problems.length) {
    // Loud, never fatal — one bad spec must not blank the catalog, the same
    // contract createRegistry keeps for one bad manifest.
    console.warn("[xray] runtime lens problems:", problems);
  }
  return createRegistry([...MANIFESTS, ...manifests], { reserved: HOST_WIRED });
}

const subscribers = new Set();

function ensureSubscribed() {
  if (_subscribed) return;
  _subscribed = true;
  // The handle is kept so a reset can actually detach. Dropping it left the old
  // closure live in the store, and every reset added another one — each rebuilding
  // the registry and bumping the generation on the same mount.
  _unsubscribe = subscribeRuntimeLenses(() => {
    _registry = null;
    _generation += 1;
    // Rebuild eagerly so every subscriber that re-renders in this tick reads the
    // SAME registry object. Lazily rebuilding here would let two components in one
    // paint disagree about the catalog, which is the drift the old freeze was
    // trying to avoid — this removes the cause instead of the reactivity.
    _registry = build();
    for (const fn of subscribers) {
      try { fn(); } catch { /* one subscriber must not block its siblings */ }
    }
  });
}

/** The merged registry. Cached; rebuilt when a mount or unmount changes it. */
export function xrayRegistry() {
  ensureSubscribed();
  if (!_registry) _registry = build();
  return _registry;
}

/**
 * The registry, re-rendering the caller when a lens mounts or unmounts.
 *
 * Components must use THIS rather than `xrayRegistry()` — a plain call reads the
 * current catalog but never hears about the next one, which is what made a mount
 * require a reload.
 */
export function useXrayRegistry() {
  return useSyncExternalStore(subscribeCatalog, xrayRegistry, xrayRegistry);
}

function subscribeCatalog(fn) {
  ensureSubscribed();
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Bumped on every catalog change. Exposed for tests and for cache keys. */
export function catalogGeneration() {
  return _generation;
}

/** Test-only: forget the cached registry and the subscription. */
export function _resetXrayRegistry() {
  if (_unsubscribe) _unsubscribe();
  _unsubscribe = null;
  _registry = null;
  _generation = 0;
  _subscribed = false;
  subscribers.clear();
}
